import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SystemHeader from './SystemHeader';
import OrderFormPreview from './OrderFormPreview';
import InventoryForm from './InventoryForm';
import { CungVeDichAPI } from '../services/API';

// Autocomplete Component
const AutocompleteSelect = ({
  options,
  value,
  onChange,
  placeholder,
  displayField,
  searchFields = [],
  disabled = false,
  maxResults = 10,
  renderOption // có thì vẽ dòng to 2 tầng (ô chọn khách lẻ); không có thì 1 dòng chữ như cũ (ô sản phẩm)
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter and limit results
  const filteredOptions = options
    .filter(option => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return searchFields.some(field => {
        const fieldValue = option[field]?.toString().toLowerCase() || '';
        return fieldValue.includes(searchLower);
      });
    })
    .slice(0, maxResults);

  // Get display text for selected value
  const getDisplayText = () => {
    if (!value) return '';
    const selected = options.find(opt => opt.id.toString() === value.toString());
    return selected ? displayField(selected) : '';
  };

  const handleSelect = (option) => {
    onChange(option.id.toString());
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(0);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : getDisplayText()}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm('');
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-3 pr-8 py-2 text-base md:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        {/* Dropdown Icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className={`absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg overflow-y-auto ${renderOption ? 'max-h-[60vh]' : 'max-h-60'}`}>
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              Không tìm thấy kết quả
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              renderOption ? (
                <div key={option.id} onClick={() => handleSelect(option)}
                     className={`px-3 py-2.5 cursor-pointer border-b border-gray-100 active:bg-green-100 ${index === highlightedIndex ? 'bg-green-50' : index % 2 ? 'bg-gray-50' : 'bg-white'}`}>
                  {renderOption(option)}
                </div>
              ) : (
              <div
                key={option.id}
                onClick={() => handleSelect(option)}
                className={`px-3 py-2 cursor-pointer text-sm ${index === highlightedIndex
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-900 hover:bg-gray-50'
                  }`}
              >
                {displayField(option)}
              </div>
              )
            ))
          )}
          {filteredOptions.length === maxResults && (
            <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200">
              Hiển thị {maxResults} kết quả đầu tiên
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Che 4 số giữa SĐT khách lẻ (0786824161 → 078****161) — tránh bị chụp màn hình lộ cả danh sách SĐT. Tìm theo SĐT vẫn dùng số đầy đủ.
export const sdtChe = (sdt) => {
  const s = String(sdt || '');
  if (s.length >= 8) return `${s.slice(0, 3)}****${s.slice(-3)}`;
  return s.length > 2 ? `${s[0]}***${s.slice(-1)}` : s; // số nhập thiếu (vd 01208) vẫn che
};

const OrderForm = ({ userInfo, onLogout, token, totalPoints, accessToken, onRefreshPoints }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('id');
  // Mở từ QL Khách lẻ: ?khach=<id> điền sẵn ô khách, ?ve=/khach-le xong thì quay về đó
  const veTrang = searchParams.get('ve') || '/don-hang';

  const [customers, setCustomers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(searchParams.get('khach') || '');
  const [orderProducts, setOrderProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showInventoryForm, setShowInventoryForm] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);

  // New customer form
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phoneNumber: '',
    facebook: ''
  });

  // Helper function to get price from priceTable based on quantity
  const getPriceByQuantity = (priceTable, quantity, fallbackPrice) => {
    // If no priceTable or empty, use fallback
    if (!priceTable || !Array.isArray(priceTable) || priceTable.length === 0) {
      return fallbackPrice || 0;
    }

    const qty = parseInt(quantity) || 1;

    // Find matching price tier
    const matchingTier = priceTable.find(tier =>
      qty >= (tier.minQuantity || 0) && qty <= (tier.maxQuantity || Infinity)
    );

    return matchingTier ? matchingTier.price : (fallbackPrice || 0);
  };

  // Function to load customers and products
  const loadCustomersAndProducts = useCallback(async () => {
    try {
      console.log('Reloading customers and products...');

      // Load customers and products in parallel
      const [customersResult, productsResult] = await Promise.all([
        CungVeDichAPI.getCustomers(accessToken, userInfo.id, 15),
        CungVeDichAPI.getProductInventory(accessToken)
      ]);

      if (customersResult.success && customersResult.customers) {
        const avaiableCustomers = customersResult.customers.map(customer => ({
          ...customer,
        }));
        setCustomers(avaiableCustomers);
      }

      if (productsResult.success && productsResult.products) {
        // Create a map for quick lookup
        const latestProductsMap = {};
        productsResult.products.forEach(p => {
          latestProductsMap[p.id.toString()] = p;
        });

        // Only show products with stock > 0
        const availableProducts = productsResult.products
          .filter(p => (parseInt(p.quantity) || 0) > 0)
          .map(product => ({
            ...product,
            stock: parseInt(product.quantity) || 0,
            priceTable: product.priceTable || [] // Include price table
          }));
        setAllProducts(availableProducts);

        // Update current order products with latest stock and prices
        setOrderProducts(prevOrderProducts =>
          prevOrderProducts.map(item => {
            const latest = latestProductsMap[item.id?.toString()];
            if (latest) {
              const newStock = parseInt(latest.quantity) || 0;
              const newPriceTable = latest.priceTable || [];
              const newFallbackPrice = latest.price || 0;
              const newPrice = getPriceByQuantity(newPriceTable, item.quantity, newFallbackPrice);

              return {
                ...item,
                stock: newStock,
                priceTable: newPriceTable,
                fallbackPrice: newFallbackPrice,
                price: newPrice
              };
            }
            return item;
          })
        );
      }
    } catch (error) {
      console.error('Error reloading data:', error);
    }
  }, [accessToken, userInfo.id]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      if (!accessToken) {
        console.warn('No access token available');
        setInitialLoading(false);
        return;
      }

      try {
        setInitialLoading(true);
        console.log('Loading initial data...');

        await loadCustomersAndProducts();

        // If editing, load order data
        if (orderId) {
          const orderResult = await CungVeDichAPI.getOrder(orderId, accessToken);
          console.log('orderResult', orderResult);
          if (orderResult.success && orderResult.order) {
            setSelectedCustomer(orderResult.order.customerId?.toString() || '');
            const orderDetails = orderResult.order.orderDetails.map(row => ({
              id: row.product ? row.product.id : null,
              name: row.product ? row.product.name : '',
              stock: row.product ? row.product.stock : '',
              quantity: row.quantity,
              price: row.unitPrice,
              priceTable: row.product ? (row.product.priceTable || []) : [],
              fallbackPrice: row.product ? (row.product.price || 0) : 0
            }));
            console.log('orderDetails: ', orderDetails);
            setOrderProducts(orderDetails);
          }
        }

      } catch (error) {
        console.error('Error loading initial data:', error);
        setMessage('Có lỗi xảy ra khi tải dữ liệu: ' + error.message);
        setMessageType('error');
      } finally {
        setInitialLoading(false);
      }
    };

    loadInitialData();
  }, [accessToken, orderId, userInfo.id, loadCustomersAndProducts]);

  const handleProductSelect = (productId) => {
    if (!productId) {
      setSelectedProductId('');
      return;
    }

    const product = allProducts.find(p => p.id.toString() === productId);
    if (!product) return;

    // Check if product already in list
    const existingProduct = orderProducts.find(p => p.id === product.id);
    if (existingProduct) {
      setMessage('Sản phẩm đã có trong danh sách');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
      setSelectedProductId('');
      return;
    }

    // Add product to the top of the list
    const initialQuantity = 1;
    const initialPrice = getPriceByQuantity(product.priceTable, initialQuantity, product.price);

    const newProduct = {
      id: product.id,
      name: product.name,
      price: initialPrice,
      quantity: initialQuantity,
      stock: product.stock,
      priceTable: product.priceTable || [], // Store priceTable for later recalculation
      fallbackPrice: product.price || 0 // Store fallback price
    };

    setOrderProducts([newProduct, ...orderProducts]);
    setSelectedProductId('');
  };

  const handleRemoveProduct = (productId) => {
    setOrderProducts(orderProducts.filter(p => p.id !== productId));
  };

  const handleQuantityChange = (productId, value) => {
    const product = orderProducts.find(p => p.id === productId);
    const maxStock = product?.stock || 0;
    const maxAllowed = Math.min(maxStock, 200);

    // Show warning if user tries to exceed maximum
    if (parseInt(value) > maxAllowed) {
      setMessage(`Số lượng tối đa là ${maxAllowed} (đơn hàng lẻ tối đa 200)`);
      setMessageType('error');
      setTimeout(() => setMessage(''), 2000);
    }

    setOrderProducts(prevProducts =>
      prevProducts.map(product =>
        product.id === productId
          ? { ...product, quantity: value }
          : product
      )
    );
  };

  const handleQuantityBlur = (productId, value) => {
    let normalizedValue = value;
    const product = orderProducts.find(p => p.id === productId);
    const maxStock = product?.stock || 0;
    const maxAllowed = Math.min(maxStock, 200); // trần 1 SP / đơn lẻ — anh Bình đổi 10 → 200 (23/09/2026)

    if (value === '' || value === null || value === undefined || isNaN(parseInt(value)) || parseInt(value) <= 0) {
      normalizedValue = 1;
    } else {
      // Clamp value between 1 and min(stock, 200)
      normalizedValue = Math.min(maxAllowed, Math.max(1, parseInt(value)));
    }

    setOrderProducts(prevProducts =>
      prevProducts.map(product => {
        if (product.id === productId) {
          // Recalculate price based on new quantity
          const newPrice = getPriceByQuantity(product.priceTable, normalizedValue, product.fallbackPrice);
          return {
            ...product,
            quantity: normalizedValue,
            price: newPrice
          };
        }
        return product;
      })
    );
  };

  // Price handlers - display value is ÷1000, stored value is actual (×1000)
  const handlePriceChange = (productId, displayValue) => {
    const numValue = parseFloat(displayValue) || 0;
    setOrderProducts(prevProducts =>
      prevProducts.map(product =>
        product.id === productId
          ? { ...product, price: numValue * 1000 }
          : product
      )
    );
  };

  const handlePriceBlur = (productId, displayValue) => {
    let numValue = parseFloat(displayValue) || 0;
    if (numValue < 0) numValue = 0;
    setOrderProducts(prevProducts =>
      prevProducts.map(product =>
        product.id === productId
          ? { ...product, price: numValue * 1000 }
          : product
      )
    );
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.facebook || !newCustomer.phoneNumber) {
      setMessage('Vui lòng nhập đầy đủ thông tin khách hàng');
      setMessageType('error');
      return;
    }

    // Validate phone number: exactly 10 digits
    if (!/^\d{10}$/.test(newCustomer.phoneNumber)) {
      setMessage('Số điện thoại phải đúng 10 chữ số');
      setMessageType('error');
      return;
    }

    try {
      const result = await CungVeDichAPI.createCustomer(newCustomer, accessToken);
      if (result.success && result.customer) {
        // SĐT đã có → API trả lại khách cũ (gắn thêm đại lý) → thay dòng cũ, không nhân đôi
        setCustomers([...customers.filter(c => c.id !== result.customer.id), result.customer]);
        setSelectedCustomer(result.customer.id.toString());
        setNewCustomer({ name: '', phoneNumber: '', facebook: '' });
        setShowAddCustomerDialog(false);
        // SĐT đã là khách lẻ (của đại lý khác) → BE trả câu báo để đại lý biết mà giữ nhất quán với khách
        setMessage(result.customer.thongBao || 'Thêm khách hàng thành công');
        setMessageType('success');
        setTimeout(() => setMessage(''), result.customer.thongBao ? 8000 : 3000);
      }
    } catch (error) {
      console.error('Error adding customer:', error);
      setMessage('Có lỗi xảy ra khi thêm khách hàng: ' + error.message);
      setMessageType('error');
    }
  };

  const calculateTotal = () => {
    return orderProducts.reduce((total, product) => {
      const quantity = parseInt(product.quantity) || 0;
      const price = parseFloat(product.price) || 0;
      return total + (quantity * price);
    }, 0);
  };

  const handleSubmit = async (e, action = 'save') => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Validation
    if (!selectedCustomer) {
      setMessage('Vui lòng chọn khách hàng');
      setMessageType('error');
      setLoading(false);
      return;
    }

    if (orderProducts.length === 0) {
      setMessage('Vui lòng thêm ít nhất một sản phẩm');
      setMessageType('error');
      setLoading(false);
      return;
    }

    try {
      const orderData = {
        customerId: parseInt(selectedCustomer),
        products: orderProducts.map(product => ({
          productId: product.id,
          quantity: Math.max(1, parseInt(product.quantity) || 1),
          unitPrice: parseFloat(product.price) || 0
        })),
        totalAmount: calculateTotal(),
        type: 1 // Retail order type
      };

      console.log('Order data to submit:', orderData);

      let result;
      if (orderId) {
        result = await CungVeDichAPI.updateOrder(orderId, orderData, accessToken);
      } else {
        result = await CungVeDichAPI.createOrder(orderData, accessToken);
      }

      if (result.success) {
        setMessage(`${orderId ? 'Cập nhật' : 'Tạo'} đơn hàng thành công!`);
        setMessageType('success');

        if (action === 'saveAndContinue' && !orderId) {
          // Reload customers and products to get latest data
          await loadCustomersAndProducts();

          // Clear form for next order
          setTimeout(() => {
            setSelectedCustomer('');
            setOrderProducts([]);
            setSelectedProductId('');
            setMessage('Đơn hàng đã lưu! Dữ liệu đã được cập nhật.');
            setMessageType('success');
            setTimeout(() => setMessage(''), 3000);
          }, 500);
        } else {
          // Navigate back to order list
          setTimeout(() => {
            navigate(veTrang);
          }, 1500);
        }
      } else {
        throw new Error(result.message || 'Lưu đơn hàng thất bại');
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      setMessage('Có lỗi xảy ra khi lưu đơn hàng. Vui lòng thử lại.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndPreview = async () => {
    setLoading(true);
    setMessage('');

    // Validation
    if (!selectedCustomer) {
      setMessage('Vui lòng chọn khách hàng');
      setMessageType('error');
      setLoading(false);
      return;
    }

    if (orderProducts.length === 0) {
      setMessage('Vui lòng thêm ít nhất một sản phẩm');
      setMessageType('error');
      setLoading(false);
      return;
    }

    try {
      const orderData = {
        customerId: parseInt(selectedCustomer),
        products: orderProducts.map(product => ({
          productId: product.id,
          quantity: Math.max(1, parseInt(product.quantity) || 1),
          unitPrice: parseFloat(product.price) || 0
        })),
        totalAmount: calculateTotal(),
        type: 1 // Retail order type
      };

      console.log('Order data to submit (save and preview):', orderData);

      let result;
      if (orderId) {
        result = await CungVeDichAPI.updateOrder(orderId, orderData, accessToken);
      } else {
        result = await CungVeDichAPI.createOrder(orderData, accessToken);
      }

      console.log('=== DEBUG: Full API Response ===');
      console.log('Result object:', result);
      console.log('Result.success:', result.success);
      console.log('Result.data:', result.data);
      console.log('Result.data.id:', result.data?.id);

      if (result.success) {
        setMessage(`${orderId ? 'Cập nhật' : 'Tạo'} đơn hàng thành công!`);
        setMessageType('success');

        // Save the created order ID from response
        if (result.data && result.data.id) {
          console.log('✅ Create order response:', result);
          console.log('✅ Setting createdOrderId to:', result.data.id);
          setCreatedOrderId(result.data.id);
        } else {
          console.log('⚠️ No order ID found in response!');
          console.log('Result structure:', JSON.stringify(result, null, 2));
        }

        // Show preview after successful save
        setTimeout(() => {
          console.log('🔍 Opening preview with createdOrderId:', result.data?.id);
          setShowImageDialog(true);
        }, 500);
      } else {
        throw new Error(result.message || 'Lưu đơn hàng thất bại');
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      setMessage('Có lỗi xảy ra khi lưu đơn hàng. Vui lòng thử lại.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle={orderId ? "Cập nhật đơn hàng" : "Tạo đơn hàng mới"}
        pageSubtitle="Quản lý đơn hàng"
        onRefreshPoints={onRefreshPoints}
      />

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border flex flex-col h-[calc(100vh-200px)]">
          <form onSubmit={handleSubmit} className="flex flex-col flex-1">
            {/* Message Display */}
            {message && (
              <div className={`mx-4 mt-3 p-2.5 rounded-md text-sm flex-shrink-0 ${messageType === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                {message}
              </div>
            )}

            {/* Customer Selection */}
            <div className="py-2 px-4 border-b border-gray-200 flex-shrink-0 bg-blue-50">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">
                    <span className="inline-flex items-center justify-center w-5 h-5 mr-1 rounded-full bg-green-600 text-white text-xs font-bold">1</span>Chọn khách hàng
                  </label>
                  <AutocompleteSelect
                    options={customers}
                    value={selectedCustomer}
                    onChange={setSelectedCustomer}
                    placeholder="Tìm kiếm khách hàng..."
                    displayField={(customer) => [customer.facebook || customer.name, sdtChe(customer.userName || customer.phone)].filter(Boolean).join(' - ')}
                    renderOption={(customer) => (
                      <>
                        <div className="text-base font-semibold text-gray-900 leading-tight">{customer.facebook || customer.name || '—'}</div>
                        <div className="text-sm text-gray-500 mt-0.5">{sdtChe(customer.userName || customer.phone)}{customer.code ? <span className="text-gray-400"> · Mã {customer.code}</span> : null}</div>
                      </>
                    )}
                    searchFields={['code', 'facebook', 'name', 'userName', 'phoneNumber', 'phone']}
                    disabled={initialLoading}
                    maxResults={50}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddCustomerDialog(true)}
                  className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors whitespace-nowrap"
                >
                  + KH
                </button>
              </div>

              {/* Selected Customer Info */}
              {/*{selectedCustomerData && (*/}
              {/*  <div className="mt-1.5 p-1.5 bg-white border border-blue-200 rounded-md">*/}
              {/*    <div className="flex items-center text-xs">*/}
              {/*      <svg className="w-3.5 h-3.5 text-blue-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
              {/*        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />*/}
              {/*      </svg>*/}
              {/*      <span className="font-medium text-gray-700">*/}
              {/*        {selectedCustomerData.name}*/}
              {/*      </span>*/}
              {/*      <span className="mx-1.5 text-gray-400">•</span>*/}
              {/*      <span className="text-gray-600">*/}
              {/*        {selectedCustomerData.phoneNumber || selectedCustomerData.phone}*/}
              {/*      </span>*/}
              {/*    </div>*/}
              {/*  </div>*/}
              {/*)}*/}
            </div>

            {/* Product Selection */}
            <div className="py-2 px-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">
                    <span className="inline-flex items-center justify-center w-5 h-5 mr-1 rounded-full bg-green-600 text-white text-xs font-bold">2</span>Chọn sản phẩm <span className="text-xs font-normal text-gray-500">(chưa có thì bấm Nhập kho)</span>
                  </label>
                  <AutocompleteSelect
                    options={allProducts}
                    value={selectedProductId}
                    onChange={handleProductSelect}
                    placeholder="Tìm kiếm sản phẩm..."
                    displayField={(product) => `${product.name} (Kho: ${product.stock})`}
                    searchFields={['name', 'code']}
                    disabled={initialLoading}
                    maxResults={100}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowInventoryForm(true)}
                  className="px-3 py-2 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors whitespace-nowrap"
                >
                  📦 Nhập kho
                </button>
              </div>
            </div>

            {/* Products List */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 pt-2 text-sm font-semibold text-gray-800"><span className="inline-flex items-center justify-center w-5 h-5 mr-1 rounded-full bg-green-600 text-white text-xs font-bold">3</span>Sửa số lượng, giá</div>
              {initialLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-gray-600">Đang tải dữ liệu...</span>
                </div>
              ) : orderProducts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Chưa có sản phẩm nào trong đơn hàng</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300">
                          Sản phẩm
                        </th>
                        <th className="px-1 py-1.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300 w-20">
                          SL
                        </th>
                        <th className="px-1 py-1.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300 w-24">
                          <div>Đơn giá</div>
                          <div className="text-[10px] font-normal normal-case text-gray-500">(x1.000)</div>
                        </th>
                        <th className="px-1 py-1.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300 w-24">
                          Thành tiền
                        </th>
                        <th className="px-1 py-1.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300 w-12">
                          Xóa
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orderProducts.map((product) => {
                        const quantity = parseInt(product.quantity) || 0;
                        const price = parseFloat(product.price) || 0;
                        const total = quantity * price;
                        const displayPrice = price ? price / 1000 : '';

                        return (
                          <tr key={product.id} className="hover:bg-gray-50">
                            <td className="px-2 py-1.5">
                              <div className="text-xs font-medium text-gray-900">
                                {product.name}
                              </div>
                            </td>
                            <td className="px-1 py-1.5">
                              <input
                                type="number"
                                min="1"
                                max={Math.min(product.stock, 200)}
                                value={product.quantity}
                                style={{ width: '55px', minHeight: '28px' }}
                                onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                                onBlur={(e) => handleQuantityBlur(product.id, e.target.value)}
                                className="px-1.5 py-0.5 text-base md:text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
                                placeholder="1"
                              />
                            </td>
                            <td className="px-1 py-1.5">
                          <div className="flex items-center">
                                <input
                                  type="number"
                                  min="0"
                                  value={displayPrice}
                                  style={{ width: '55px', minHeight: '28px' }}
                                  onChange={(e) => handlePriceChange(product.id, e.target.value)}
                                  onBlur={(e) => handlePriceBlur(product.id, e.target.value)}
                                  className="px-1 py-0.5 text-base md:text-xs border border-gray-300 rounded-l focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
                                  placeholder="0"
                                />
                                <span className="px-1 py-0.5 text-base md:text-xs bg-gray-100 border border-l-0 border-gray-300 rounded-r text-gray-500 select-none" style={{ minHeight: '28px', display: 'flex', alignItems: 'center' }}>.000</span>
                              </div>
                            </td>
                            <td className="px-1 py-1.5">
                              <div className="px-1.5 py-0.5 text-xs bg-blue-50 border border-blue-200 rounded text-center font-semibold text-blue-700" style={{ minHeight: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {total.toLocaleString()}₫
                              </div>
                            </td>
                            <td className="px-1 py-1.5 text-center">
                              <button
                                type="button"
                                onClick={() => handleRemoveProduct(product.id)}
                                className="text-red-600 hover:text-red-800 focus:outline-none"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Total Amount - Pinned at bottom */}
            <div className="flex-shrink-0 border-t-2 border-gray-300 bg-gray-50 px-4 py-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-gray-800">Tổng tiền:</span>
                <span className="text-base font-bold text-orange-600">
                  {calculateTotal().toLocaleString()}₫
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!loading) {
                      navigate(veTrang);
                    }
                  }}
                  disabled={loading}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSaveAndPreview}
                  disabled={loading || orderProducts.length === 0 || !selectedCustomer}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-md font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      <span>Đang lưu...</span>
                    </div>
                  ) : (
                    <><span className="inline-flex items-center justify-center w-5 h-5 mr-1 rounded-full bg-white text-purple-700 text-xs font-bold">4</span>Tạo đơn + ảnh</>
                  )}
                </button>
                {!orderId && (
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'saveAndContinue')}
                    disabled={loading || orderProducts.length === 0 || !selectedCustomer}
                    className="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded-md font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        <span>Đang lưu...</span>
                      </div>
                    ) : (
                      'Nhập đơn mới'
                    )}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading || orderProducts.length === 0 || !selectedCustomer}
                  className={`${!orderId ? 'flex-1' : 'flex-1'} px-4 py-2 text-sm bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      <span>Đang xử lý...</span>
                    </div>
                  ) : (
                    orderId ? 'Về danh sách' : 'Về danh sách'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Add Customer Dialog */}
      {showAddCustomerDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Thêm khách hàng mới</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Tên khách hàng <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newCustomer.facebook}
                  onChange={(e) => setNewCustomer({ ...newCustomer, facebook: e.target.value })}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nhập tên khách hàng"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Số điện thoại <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={newCustomer.phoneNumber}
                  onChange={(e) => {
                    // Only allow digits, strip everything else (including spaces)
                    const cleaned = e.target.value.replace(/\D/g, '');
                    // Limit to 10 digits
                    const limited = cleaned.slice(0, 10);
                    setNewCustomer({ ...newCustomer, phoneNumber: limited });
                  }}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nhập số điện thoại (10 số)"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-4 pb-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddCustomerDialog(false);
                  setNewCustomer({ name: '', phoneNumber: '', facebook: '' });
                  setMessage('');
                }}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleAddCustomer}
                className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Dialog */}
      <OrderFormPreview
        isOpen={showImageDialog}
        onClose={() => {
          setShowImageDialog(false);
          setCreatedOrderId(null);
          navigate(veTrang);
        }}
        orderId={createdOrderId || orderId}
        userInfo={userInfo}
      />

      {/* Inventory Form Modal */}
      {showInventoryForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-50">
          <InventoryForm
            userInfo={userInfo}
            onLogout={onLogout}
            token={token}
            totalPoints={totalPoints}
            accessToken={accessToken}
            onRefreshPoints={onRefreshPoints}
            onClose={() => {
              setShowInventoryForm(false);
              // Reload products after closing inventory form
              loadCustomersAndProducts();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default OrderForm;

